import React from 'react';
import { Button, Modal, Table, Form } from 'react-bootstrap';
import MilkDeliveryApi from '../../api/MilkDeliveryApi';
import AuthorizedPage from '../../component/AuthorizedPage';

interface Vehicle {
  id?: number;
  licencePlateCode?: string;
  brand?: string;
  model?: string;
  productionYear?: number;
  yearOfPurchase?: number;
  tankerCount?: number;
  tankerVolumePerUnit?: number;
  totalVolume?: number;
}

interface State {
  vehicles: Vehicle[];
  showModal: boolean;
  editingVehicle: Vehicle | null;
  loading: boolean;
}

const defaultVehicle: Vehicle = {
  brand: '',
  model: '',
  licencePlateCode: '',
  productionYear: new Date().getFullYear(),
  yearOfPurchase: new Date().getFullYear(),
  tankerCount: 0,
  tankerVolumePerUnit: 0,
  totalVolume: 0,
};

export default class MilkVehicleManagement extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);
    this.state = {
      vehicles: [],
      showModal: false,
      editingVehicle: null,
      loading: false,
    };
  }

  componentDidMount(): void {
    this.loadVehicles();
  }

  async loadVehicles() {
    this.setState({ loading: true });
    try {
      const result = await MilkDeliveryApi.listVehicles();
      if (result?.success && result.data) {
        this.setState({ vehicles: result.data });
      } else {
        console.error('Araçlar yüklenemedi:', result);
        this.setState({ vehicles: [] });
      }
    } catch (err) {
      console.error('Araçlar yüklenirken hata:', err);
      this.setState({ vehicles: [] });
    } finally {
      this.setState({ loading: false });
    }
  }

  openEditModal = (vehicle?: Vehicle) => {
    this.setState({
      editingVehicle: vehicle ? { ...vehicle } : { ...defaultVehicle },
      showModal: true,
    });
  };

  handleChange = (e: React.ChangeEvent<any>) => {
    if (!this.state.editingVehicle) return;

    const { name, value } = e.target;
    const numericFields = [
      'productionYear',
      'yearOfPurchase',
      'tankerCount',
      'tankerVolumePerUnit',
      'totalVolume',
    ];
    this.setState(prevState => ({
      editingVehicle: {
        ...prevState.editingVehicle!,
        [name]: numericFields.includes(name) ? (value === '' ? undefined : Number(value)) : value,
      },
    }));
  };

  computeTotalVolume(entity: Vehicle) {
    const count = entity.tankerCount ?? 0;
    const perUnit = entity.tankerVolumePerUnit ?? 0;
    return count * perUnit;
  }

  handleSave = async () => {
    const { editingVehicle } = this.state;
    if (!editingVehicle) return;

    const totalVolume = this.computeTotalVolume(editingVehicle);
    const payload: Vehicle = {
      ...editingVehicle,
      totalVolume,
    };

    try {
      const result = await MilkDeliveryApi.createOrUpdateVehicle(payload);
      if (result?.success) {
        await this.loadVehicles();
        this.setState({ showModal: false, editingVehicle: null });
      } else {
        console.error('Kaydetme başarısız:', result);
      }
    } catch (err) {
      console.error('Kaydetme hatası:', err);
    }
  };

  renderVehicleTable() {
    return (
      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Plaka</th>
            <th>Marka</th>
            <th>Model</th>
            <th>Üretim Yılı</th>
            <th>Satın Alma Yılı</th>
            <th>Tanker Adedi</th>
            <th>Tanker Hacmi (L)</th>
            <th>Toplam Hacim (L)</th>
            <th>İşlem</th>
          </tr>
        </thead>
        <tbody>
          {this.state.vehicles.map(vehicle => (
            <tr key={vehicle.id}>
              <td>{vehicle.licencePlateCode}</td>
              <td>{vehicle.brand}</td>
              <td>{vehicle.model}</td>
              <td>{vehicle.productionYear}</td>
              <td>{vehicle.yearOfPurchase}</td>
              <td>{vehicle.tankerCount ?? '-'}</td>
              <td>{vehicle.tankerVolumePerUnit ?? '-'}</td>
              <td>{vehicle.totalVolume ?? this.computeTotalVolume(vehicle)}</td>
              <td>
                <Button size="sm" variant="outline-primary" onClick={() => this.openEditModal(vehicle)}>
                  Düzenle
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderVehicleModal() {
    const { editingVehicle } = this.state;
    if (!editingVehicle) return null;

    return (
      <Modal show={this.state.showModal} onHide={() => this.setState({ showModal: false, editingVehicle: null })}>
        <Modal.Header closeButton>
          <Modal.Title>Süt Aracı Bilgileri</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Plaka</Form.Label>
              <Form.Control name="licencePlateCode" value={editingVehicle.licencePlateCode || ''} onChange={this.handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Marka</Form.Label>
              <Form.Control name="brand" value={editingVehicle.brand || ''} onChange={this.handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Model</Form.Label>
              <Form.Control name="model" value={editingVehicle.model || ''} onChange={this.handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Üretim Yılı</Form.Label>
              <Form.Control name="productionYear" type="number" value={editingVehicle.productionYear || ''} onChange={this.handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Satın Alma Yılı</Form.Label>
              <Form.Control name="yearOfPurchase" type="number" value={editingVehicle.yearOfPurchase || ''} onChange={this.handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tanker Adedi</Form.Label>
              <Form.Control name="tankerCount" type="number" value={editingVehicle.tankerCount || ''} onChange={this.handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tanker Hacmi (Litre / tanker)</Form.Label>
              <Form.Control name="tankerVolumePerUnit" type="number" value={editingVehicle.tankerVolumePerUnit || ''} onChange={this.handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Toplam Hacim (L) — otomatik</Form.Label>
              <Form.Control readOnly value={this.computeTotalVolume(editingVehicle)} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => this.setState({ showModal: false, editingVehicle: null })}>
            İptal
          </Button>
          <Button variant="primary" onClick={this.handleSave}>
            Kaydet
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  render() {
    return (
      <AuthorizedPage role="ORDER_MANAGER" isLoading={this.state.loading}>
        <div className="container mt-4">
          <h4>Süt Araçları Yönetimi</h4>
          <Button className="mb-3" onClick={() => this.openEditModal()}>
            + Araç Ekle
          </Button>
          {this.renderVehicleTable()}
          {this.renderVehicleModal()}
        </div>
      </AuthorizedPage>
    );
  }
}
